import { useState, useCallback } from "react";
import { useGraphQLFetch } from "./use-graphql-fetch";
import { toast } from "sonner";

// Types matching the backend GraphQL schema
export interface StockMovementOutput {
  movementID: number;
  movementReference: string;
  movementDate: string;
  movementType: string;
  movementSource: string;
  stockCode: string;
  stockDescription: string;
  locationCode: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  userName: string;
  notes: string | null;
  supplier: string | null;
  invoiceNumber: string | null;
  customerReference: string | null;
  createdAt: string;
  modifiedAt: string;
}

// Frontend types (matching existing mock data types)
export interface StockMovement {
  id: string;
  reference: string;
  date: Date;
  type:
    | "Sales"
    | "Purchase"
    | "Opening Balance"
    | "Write-off"
    | "Transfer"
    | "Manual Adjustment"
    | "Stock Take Adjustment";
  source: "Manual" | "ERP Sync" | "System Generated";
  stockCode: string;
  stockDescription: string;
  location: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  userName: string;
  notes: string | null;
}

// Pagination info from GraphQL connection
export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

// GraphQL response types
interface StockMovementConnection {
  items: StockMovementOutput[];
  pageInfo: PageInfo;
  totalCount?: number;
}

interface StockMovementsGraphQLResponse {
  data?: {
    stockMovements?: {
      stockMovementList?: StockMovementConnection;
    };
  };
  errors?: Array<{ message: string }>;
}

// Map backend movement type to frontend type
const mapMovementType = (backendType: string): StockMovement["type"] => {
  const typeMap: Record<string, StockMovement["type"]> = {
    Sales: "Sales",
    Purchase: "Purchase",
    OpeningBalance: "Opening Balance",
    "Opening Balance": "Opening Balance",
    Writeoff: "Write-off",
    "Write-off": "Write-off",
    Transfer: "Transfer",
    ManualAdjustment: "Manual Adjustment",
    "Manual Adjustment": "Manual Adjustment",
    StockTakeAdjustment: "Stock Take Adjustment",
    "Stock Take Adjustment": "Stock Take Adjustment",
  };
  return typeMap[backendType] || "Manual Adjustment";
};

// Map backend source to frontend type
const mapMovementSource = (backendSource: string): StockMovement["source"] => {
  const sourceMap: Record<string, StockMovement["source"]> = {
    Manual: "Manual",
    ERPSync: "ERP Sync",
    "ERP Sync": "ERP Sync",
    SystemGenerated: "System Generated",
    "System Generated": "System Generated",
  };
  return sourceMap[backendSource] || "Manual";
};

// Map backend stock movement to frontend type
const mapStockMovementToFrontend = (node: StockMovementOutput): StockMovement => {
  return {
    id: `MOV${node.movementID.toString().padStart(3, "0")}`,
    reference: node.movementReference,
    date: new Date(node.movementDate),
    type: mapMovementType(node.movementType),
    source: mapMovementSource(node.movementSource),
    stockCode: node.stockCode,
    stockDescription: node.stockDescription,
    location: node.locationCode,
    quantity: node.quantity,
    unitPrice: node.unitPrice,
    lineTotal: node.lineTotal,
    userName: node.userName,
    notes: node.notes,
  };
};

// Input type for creating a stock movement
export interface CreateStockMovementInput {
  stockCode: string;
  stockDescription: string;
  movementType: string;
  movementSource: string;
  locationCode?: string;
  quantity: number;
  unitPrice: number;
  notes?: string;
  supplier?: string;
  invoiceNumber?: string;
  customerReference?: string;
  userName: string;
}

// GraphQL mutation for creating a stock movement
// Only use IntEntityId fragment - UnexpectedError type doesn't have queryable fields
const CREATE_STOCK_MOVEMENT_MUTATION = `
  mutation CreateStockMovement($stockMovement: StockMovementInput!) {
    stockMovements {
      stockMovementRecords {
        create(stockMovement: $stockMovement) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL query for stock movements with filters and sorting
const STOCK_MOVEMENTS_QUERY = `
  query GetStockMovements(
    $first: Int, 
    $after: String, 
    $filter: StockMovementFilterInput,
    $sort: [StockMovementSortSortByInput!]
  ) {
    stockMovements {
      stockMovementList(
        first: $first, 
        after: $after, 
        filter: $filter,
        sort: $sort
      ) {
        items {
          movementID
          movementReference
          movementDate
          movementType
          movementSource
          stockCode
          stockDescription
          locationCode
          quantity
          unitPrice
          lineTotal
          userName
          notes
          supplier
          invoiceNumber
          customerReference
          createdAt
          modifiedAt
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

// Sort direction type (matching backend OrderType enum)
export type SortDirection = "Ascending" | "Descending";

// Sort field enum values (matching backend StockMovementSort enum)
export type StockMovementSortField =
  | "MovementDate"
  | "MovementReference"
  | "MovementType"
  | "MovementSource"
  | "StockCode"
  | "Quantity"
  | "UnitPrice"
  | "LineTotal";

// Sort input for stock movements - uses field/order structure expected by backend
export interface StockMovementSortInput {
  field: StockMovementSortField;
  order: SortDirection;
}

// Filter input for stock movements
export interface StockMovementFilterInput {
  movementReference?: { contains?: string };
  movementType?: { eq?: string };
  movementSource?: { eq?: string };
  stockCode?: { contains?: string };
  stockDescription?: { contains?: string };
  locationCode?: { contains?: string };
  userName?: { contains?: string };
}

export const useStockMovements = () => {
  const { fetchGraphQL } = useGraphQLFetch();

  // Stock movements state
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Fetch stock movements with filters, sort, and pagination
  const fetchMovements = useCallback(
    async (
      options: {
        first?: number;
        after?: string;
        filter?: StockMovementFilterInput;
        sort?: StockMovementSortInput;
      } = {},
    ): Promise<{ movements: StockMovement[]; pageInfo: PageInfo | null }> => {
      setIsLoading(true);
      setError(null);

      const { first = 25, after, filter, sort } = options;

      // Convert sort to GraphQL array format
      const sortArray = sort ? [sort] : undefined;

      try {
        const result = await fetchGraphQL<StockMovementsGraphQLResponse["data"]>(STOCK_MOVEMENTS_QUERY, {
          first,
          after,
          filter,
          sort: sortArray,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const connection = result.data?.stockMovements?.stockMovementList;
        const movementList = connection?.items?.map((item) => mapStockMovementToFrontend(item)) || [];
        const pageInfoResult = connection?.pageInfo || null;

        setMovements(movementList);
        setPageInfo(pageInfoResult);

        return { movements: movementList, pageInfo: pageInfoResult };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch stock movements";
        setError(message);
        console.error(err);
        toast.error(`Failed to load stock movements from backend: ${message}`);
        return { movements: [], pageInfo: null };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchGraphQL],
  );

  // Fetch next page of movements
  const fetchNextPage = useCallback(
    async (
      options: {
        first?: number;
        filter?: StockMovementFilterInput;
        sort?: StockMovementSortInput;
      } = {},
    ): Promise<{ movements: StockMovement[]; pageInfo: PageInfo | null }> => {
      if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) {
        return { movements, pageInfo };
      }

      return fetchMovements({
        ...options,
        after: pageInfo.endCursor,
      });
    },
    [fetchMovements, movements, pageInfo],
  );

  // Create a new stock movement
  const createMovement = useCallback(
    async (input: CreateStockMovementInput): Promise<{ success: boolean; id?: number; error?: string }> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchGraphQL<{
          stockMovements?: {
            stockMovementRecords?: {
              create?: { 
                __typename?: string; 
                id?: number;
              };
            };
          };
        }>(CREATE_STOCK_MOVEMENT_MUTATION, {
          stockMovement: input,
        });

        if (result.errors && result.errors.length > 0) {
          const errorMessage = result.errors[0].message;
          return { success: false, error: errorMessage };
        }

        const createResult = result.data?.stockMovements?.stockMovementRecords?.create;

        if (createResult?.__typename === "IntEntityId" && createResult?.id) {
          return { success: true, id: createResult.id };
        }

        if (createResult?.__typename) {
          return { success: false, error: `Backend error: ${createResult.__typename}. Check server logs for details.` };
        }

        return { success: false, error: "Unknown error occurred - no response data" };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create stock movement";
        setError(message);
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchGraphQL],
  );

  return {
    movements,
    pageInfo,
    isLoading,
    fetchMovements,
    fetchNextPage,
    createMovement,
    error,
  };
};
